import React, { useMemo, useRef, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillResize from 'quill-resize-module';
import { api } from '../lib/api';

// Register the resize module once so images (and embeds) get drag handles to
// resize them like in Word. Guarded so hot-reload doesn't re-register.
if (!Quill.imports['modules/resize']) {
  Quill.register('modules/resize', QuillResize);
}

/**
 * Rich-text editor reutilizable para Noticias, Eventos y Newsletter.
 * - Negritas, cursiva, listas, links
 * - Inserta imágenes (sube al backend via /api/upload-image)
 * - H2/H3, blockquote, alineación, separadores
 *
 * Props:
 *   value: string (HTML)
 *   onChange: (html: string) => void
 *   placeholder?: string
 *   testid?: string
 *   minHeight?: number (default 220)
 */
export default function RichTextEditor({ value, onChange, placeholder, testid, minHeight = 220, uploadUrl = '/upload-image', allowMedia = true }) {
  const quillRef = useRef(null);

  // IMPORTANT: do NOT transform the HTML here. Mutating the value inside
  // onChange (e.g. replacing &nbsp;) creates an infinite render loop with the
  // controlled ReactQuill (the prop value never converges with the editor
  // content), which FREEZES the editor. Non-breaking spaces are normalised at
  // render time in BioContent instead, so wrapping still works.
  const handleChange = (html) => {
    onChange(html || '');
  };

  // Clean pasted content (from Word/PDF/web): normalise non-breaking spaces to
  // regular spaces (so text wraps) and strip inline color/font/size styling so
  // pasted text adopts the editor's default look — keeping basic formatting
  // (bold, italic, lists, headings, links, alignment). Registered once the
  // editor mounts.
  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor || !editor.clipboard) return;
    editor.clipboard.addMatcher(Node.TEXT_NODE, (_node, delta) => {
      delta.ops = (delta.ops || []).map((op) =>
        typeof op.insert === 'string'
          ? { ...op, insert: op.insert.replace(/\u00a0/g, ' ') }
          : op,
      );
      return delta;
    });
    const STRIP = ['color', 'background', 'font', 'size'];
    editor.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
      (delta.ops || []).forEach((op) => {
        if (op.attributes) {
          STRIP.forEach((a) => { delete op.attributes[a]; });
          if (Object.keys(op.attributes).length === 0) delete op.attributes;
        }
      });
      return delta;
    });
  }, []);

  const imageHandler = useMemo(() => async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const form = new FormData();
      form.append('file', file);
      try {
        const { data } = await api.post(uploadUrl, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url = data?.url;
        if (!url) throw new Error('No URL returned');
        const editor = quillRef.current?.getEditor();
        const range = editor?.getSelection(true);
        editor?.insertEmbed(range?.index ?? 0, 'image', url, 'user');
        editor?.setSelection((range?.index ?? 0) + 1);
      } catch (err) {
        alert('No se pudo subir la imagen: ' + (err?.response?.data?.detail || err.message));
      }
    };
    input.click();
  }, [uploadUrl]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
        allowMedia ? ['blockquote', 'link', 'image'] : ['blockquote'],
        ['clean'],
      ],
      handlers: allowMedia ? { image: imageHandler } : {},
    },
    resize: {
      locale: {
        floatLeft: 'Izquierda',
        floatRight: 'Derecha',
        center: 'Centrar',
        restore: 'Tamaño original',
      },
    },
    clipboard: { matchVisual: false },
  }), [imageHandler, allowMedia]);

  // When media is disabled, whitelist ONLY plain-text formatting so links and
  // images are dropped even when pasted (guarantees "puro texto con formato").
  const formats = useMemo(() => (
    allowMedia
      ? undefined
      : ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'align', 'blockquote']
  ), [allowMedia]);

  return (
    <div data-testid={testid} className="rich-text-editor" style={{ minHeight }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        style={{ minHeight }}
      />
    </div>
  );
}
